import moment from 'moment';
import React from 'react';

import {
  ACTIVE_BILLING_PLATFORM_PLANS,
  LEGACY_BILLING_PLANS,
} from 'src/constants';
import { useCustomerSubscription } from 'src/hooks/useCustomerSubscription';
import { useGetCurrentPlanQuery } from 'src/services/api/studioCreditApi';
import { getPlanItem } from 'src/utils/subscriptionPlanItem';

export const usePlanInfo = () => {
  const { stripeCustomer, customerSubscription } = useCustomerSubscription();
  // The backend resolver names plans the price-id heuristics below cannot
  // (bespoke `_ent` customs, anonymous `price_1…` prices); the heuristics stay
  // as the synchronous fallback while the query is in flight.
  const { data: currentPlan } = useGetCurrentPlanQuery();

  const activeBillingPlans = Object.values(ACTIVE_BILLING_PLATFORM_PLANS);

  const {
    planName,
    currentPeriodEndDate,
    cancelAtPeriodEnd,
    isLegacyEnterprisePlan,
    isLegacyPlan,
  } = React.useMemo(() => {
    // The backend's resolved plan is the source of truth; until it has
    // answered, the subscription itself identifies the plan, resolved past any
    // add-on items. The price-ID heuristics below only fill the name while the
    // resolved display name is absent.
    const planPriceId =
      (currentPlan
        ? currentPlan.planPriceId
        : getPlanItem(customerSubscription)?.price.id) ?? '';

    let plan = '';
    if (planPriceId.includes('pro')) {
      plan = 'Professional plan';
    } else if (planPriceId.includes('basic')) {
      plan = 'Basic plan';
    } else if (planPriceId.includes('standard')) {
      plan = 'Standard plan';
    } else if (planPriceId.includes('enterprise')) {
      plan = 'Enterprise plan';
    } else if (planPriceId.includes('starter')) {
      plan = 'Starter Plan';
    } else if (planPriceId.includes('professional')) {
      plan = 'Professional Plan';
    } else if (planPriceId.includes('advanced')) {
      plan = 'Advanced Plan';
    }

    const periodEndDate = customerSubscription
      ? moment(new Date(customerSubscription.current_period_end * 1000)).format(
          'MMMM Do, YYYY',
        )
      : undefined;

    return {
      planName: currentPlan?.planDisplayName || plan,
      currentPeriodEndDate: periodEndDate,
      cancelAtPeriodEnd: Boolean(customerSubscription?.cancel_at_period_end),
      // A trial has no customer subscription and is not legacy. Otherwise the
      // backend's classification decides, with the platform price list as the
      // answer until it has loaded.
      isLegacyPlan:
        Boolean(customerSubscription) &&
        (currentPlan
          ? !currentPlan.isSelfServe
          : !activeBillingPlans.includes(planPriceId)),
      isLegacyEnterprisePlan: planPriceId === LEGACY_BILLING_PLANS.ENTERPRISE,
    };
  }, [stripeCustomer, customerSubscription, currentPlan]);

  return {
    planName,
    currentPeriodEndDate,
    cancelAtPeriodEnd,
    isLegacyEnterprisePlan,
    isLegacyPlan,
  };
};
